import {
  createOrUpdateFPN,
  createOrUpdateFPNAdditionalInfo,
  createOrUpdateFPNAttributes,
  createOrUpdateFPNCountryKeyDates,
  createOrUpdateFPNOverview,
  createOrUpdateRPNAdditionalInfo,
  createOrUpdateRPNAttributes,
  createOrUpdateRPNCountryKeyDates,
  createOrUpdateRPNOverview,
  createRPN,
  post_rpn_fpn_rule,
  updateRPN,
} from 'src/graphql/mutations';
import {
  listFPNDetails,
  listFPNSkus,
  listRPNAssortments,
  listRPNDetails,
  listRPNSkus,
  listRPNVersions,
} from 'src/graphql/queries';
import { FPNDetails, Skus as FPNSkus } from 'src/types/fpn/fpnDetails.type';
import { FormattedCreateRpnValues, FormattedUpdateRpnValues } from 'src/types/rpn/createRpn.types';
import { RPNDetails, RpnAssortment, RpnVersion, Skus } from 'src/types/rpn/rpnDetails.type';
import {
  FpnAdditionalInfoMutation,
  FpnAttributesMutation,
  FpnCountryKeyDatesMutation,
  FpnOverviewMutation,
  MutationRecordCountResponse,
  RpnAdditionalInfoMutation,
  RpnAttributesMutation,
  RpnCountryKeyDatesMutation,
  RpnOverviewMutation,
} from 'src/types/rpn/rpnFpnMutation.types';
import { formatApiError } from '../../utils/errorUtils';
import { LambdaResultShape, throwIfLambdaError } from '../../utils/lambdaResultUtils';
import { logger } from '../../utils/logger';
import { fetchGraphQLRequest } from './clients/graphqlClient';

/**
 * Response type for post_rpn_fpn_rule mutation (old-style CreateStatus)
 */
interface PostRpnFpnRuleResponse {
  body?: string | null;
  error?: string | null;
  statusCode?: number | null;
  updatedCodes?: string[] | null;
}

/**
 * Product Lines Service
 * Handles Product Line, RPN, and FPN API operations
 *
 * Note: AGM-related operations have been moved to agmService.ts
 */

// ============================================================================
// Product Line Operations
// ============================================================================

/**
 * Fetches product line details by ID
 * @param productLineId - The ID of the product line to fetch
 * @returns Promise resolving to product line details
 * @throws Error if API call fails
 */
export const fetchProductLineDetailsByIdAPI = async (productLineId: number): Promise<unknown> => {
  try {
    logger.info(`Fetching product line ${productLineId} from API`);

    // TODO: Implement actual API call
    const productLine = null;

    logger.info(`Fetched product line ${productLineId} from API`);
    return productLine;
  } catch (error) {
    logger.error(`Error fetching product line ${productLineId} from API:`, { error });
    throw formatApiError('Failed to fetch product line', error);
  }
};

// ============================================================================
// RPN Operations
// ============================================================================

/**
 * Response type for createRPN and updateRPN mutations
 */
interface RPNMutationResponse {
  is_pplsid_available: boolean;
  number_of_records_updated: number;
  rpn_id: number;
}

/**
 * Return type for createRpnAPI and updateRpnAPI
 */
export interface RpnMutationResult {
  isPplsIdAvailable: boolean;
  rpnId: number;
}

/**
 * Creates a new RPN (Retail Product Name) program
 * @param rpnData - The RPN data to create
 * @returns Promise resolving to mutation result with isPplsIdAvailable flag
 * @throws Error if API call fails
 */
export const createRpnAPI = async (rpnData: FormattedCreateRpnValues): Promise<RpnMutationResult> => {
  try {
    logger.info('Creating new RPN program', { rpnData });

    const result = await fetchGraphQLRequest<{ createRPN: RPNMutationResponse }>({
      query: createRPN,
      variables: { input: rpnData },
      requestName: 'createRPN',
      requestType: 'mutation',
      auditConfig: { pageName: 'create_rpn' },
    });
    const response = result.createRPN;

    logger.info('RPN program created successfully', {
      rpnId: response?.rpn_id,
      isPplsIdAvailable: response?.is_pplsid_available,
      numberOfRecordsUpdated: response?.number_of_records_updated,
    });

    return {
      isPplsIdAvailable: response?.is_pplsid_available ?? false,
      rpnId: response?.rpn_id ?? 0,
    };
  } catch (error) {
    logger.error('Error creating RPN program:', { error });
    throw formatApiError('Failed to create RPN program', error);
  }
};

/**
 * Updates an existing RPN program
 * @param rpnData - The RPN data to update
 * @returns Promise resolving to mutation result with isPplsIdAvailable flag
 * @throws Error if API call fails
 */
export const updateRpnAPI = async (rpnData: FormattedUpdateRpnValues): Promise<RpnMutationResult> => {
  try {
    logger.info('Updating RPN program', { rpnData });

    const result = await fetchGraphQLRequest<{ updateRPN: RPNMutationResponse }>({
      query: updateRPN,
      variables: { input: rpnData },
      requestName: 'updateRPN',
      requestType: 'mutation',
      auditConfig: { pageName: 'update_rpn' },
    });
    const response = result.updateRPN;

    logger.info('RPN program updated successfully', {
      rpnId: response?.rpn_id,
      isPplsIdAvailable: response?.is_pplsid_available,
      numberOfRecordsUpdated: response?.number_of_records_updated,
    });

    return {
      isPplsIdAvailable: response?.is_pplsid_available ?? false,
      rpnId: response?.rpn_id ?? 0,
    };
  } catch (error) {
    logger.error('Error updating RPN program:', { error });
    throw formatApiError('Failed to update RPN program', error);
  }
};

/**
 * Response type for createOrUpdateFPN mutation
 */
interface CreateFPNResponse extends LambdaResultShape {
  number_of_records_updated: number;
  created_fpn_ids: number[];
  deleted_fpn_ids: number[];
}

/**
 * Individual rule record for the RPN FPN rule payload.
 */
export interface RpnFpnRuleRecord {
  attribute_type: string;
  concat_order: number;
  user_id: string;
  verity_rpn_id: number;
}

/**
 * Posts the RPN-level FPN rule by sending pre-built rule records to the API.
 * The caller is responsible for parsing the PL FPN rule string into structured records.
 *
 * @param rulePayload - Array of rule records with attribute_type, concat_order, user_id, verity_rpn_id
 * @returns Promise resolving when the rule is posted
 * @throws Error if API call fails or returns non-200 status
 */
export const postRpnFpnRuleAPI = async (rulePayload: RpnFpnRuleRecord[]): Promise<void> => {
  const rpnId = rulePayload[0]?.verity_rpn_id;
  try {
    if (rulePayload.length === 0) {
      logger.info('Empty rule payload, skipping RPN FPN rule posting');
      return;
    }

    logger.info('Posting RPN FPN rule', { rpnId, ruleCount: rulePayload.length, rulePayload });

    const result = await fetchGraphQLRequest<{ post_rpn_fpn_rule: PostRpnFpnRuleResponse }>({
      query: post_rpn_fpn_rule,
      variables: { input: rulePayload },
      requestName: 'post_rpn_fpn_rule',
      requestType: 'mutation',
      auditConfig: { pageName: 'post_rpn_fpn_rule' },
    });

    const response = result.post_rpn_fpn_rule;

    if (response?.statusCode && response.statusCode !== 200) {
      logger.error('RPN FPN rule posting returned non-200 status', { rpnId, response });
      throw new Error(response?.error || 'Failed to post RPN FPN rule');
    }

    logger.info('RPN FPN rule posted successfully', { rpnId });
  } catch (error) {
    logger.error('Error posting RPN FPN rule:', { error, rpnId });
    throw formatApiError('Failed to post RPN FPN rule', error);
  }
};

/**
 * Creates new FPNs (Finance Program Names) under an RPN
 * @param rpnId - The RPN ID to create FPNs under
 * @param fpnNames - Array of FPN names to create
 * @param updatedBy - User ID performing the action
 * @returns Promise resolving to created FPN IDs
 * @throws Error if API call fails
 */
export const createFpnAPI = async (rpnId: number, fpnNames: string[], updatedBy: string): Promise<number[]> => {
  try {
    logger.info('Creating new FPN programs', { rpnId, fpnNames, updatedBy });

    const result = await fetchGraphQLRequest<{ createOrUpdateFPN: CreateFPNResponse }>({
      query: createOrUpdateFPN,
      variables: {
        input: {
          rpn_id: rpnId,
          fpn_names: fpnNames,
          updated_by: updatedBy,
        },
      },
      requestName: 'createOrUpdateFPN',
      requestType: 'mutation',
    });

    const response = result.createOrUpdateFPN;
    throwIfLambdaError(response);
    const createdFpnIds = response?.created_fpn_ids || [];

    logger.info('FPN programs created successfully', {
      numberOfRecordsUpdated: response?.number_of_records_updated,
      createdFpnIds,
    });

    return createdFpnIds;
  } catch (error) {
    logger.error('Error creating FPN programs:', { error, rpnId, fpnNames });
    throw formatApiError('Failed to create FPN programs', error);
  }
};

/**
 * Deletes existing FPNs (Finance Program Names) under an RPN
 * @param rpnId - The RPN ID containing the FPNs to delete
 * @param fpnNames - Array of FPN names to delete
 * @param updatedBy - User ID performing the action
 * @returns Promise resolving to deleted FPN IDs
 * @throws Error if API call fails
 */
export const deleteFpnAPI = async (rpnId: number, fpnNames: string[], updatedBy: string): Promise<number[]> => {
  try {
    logger.info('Deleting FPN programs', { rpnId, fpnNames, updatedBy });

    const result = await fetchGraphQLRequest<{ createOrUpdateFPN: CreateFPNResponse }>({
      query: createOrUpdateFPN,
      variables: {
        input: {
          rpn_id: rpnId,
          fpn_names: [], // Empty array for creation
          fpn_names_to_delete: fpnNames,
          updated_by: updatedBy,
        },
      },
      requestName: 'createOrUpdateFPN',
      requestType: 'mutation',
    });

    const response = result.createOrUpdateFPN;
    throwIfLambdaError(response);
    const deletedFpnIds = response?.deleted_fpn_ids || [];

    logger.info('FPN programs deleted successfully', {
      numberOfRecordsUpdated: response?.number_of_records_updated,
      deletedFpnIds,
    });

    return deletedFpnIds;
  } catch (error) {
    logger.error('Error deleting FPN programs:', { error, rpnId, fpnNames });
    throw formatApiError('Failed to delete FPN programs', error);
  }
};

/**
 * Fetches RPN details by product line ID and RPN ID
 * @param productLineId - The ID of the product line
 * @param rpnId - The ID of the RPN to fetch
 * @returns Promise resolving to RPN details
 * @throws Error if API call fails
 */
export const fetchRPNDetailsAPI = async (productLineId: number, rpnId: number): Promise<RPNDetails> => {
  try {
    logger.info(`Fetching RPN details for product line ${productLineId}, RPN ${rpnId} from API`);

    // Fetch all data in parallel for better performance
    const [listRPNDetailsResult, rpnVersionsResult, rpnAssortmentsResult, rpnSkusResult] = await Promise.all([
      fetchGraphQLRequest<{ listRPNDetails: Omit<RPNDetails, 'agm_data'> }>({
        query: listRPNDetails,
        variables: { input: { rpn_id: rpnId } },
        requestName: 'listRPNDetails',
        requestType: 'query',
      }),
      fetchGraphQLRequest<{ listRPNVersions: RpnVersion[] }>({
        query: listRPNVersions,
        variables: { input: { rpn_id: rpnId } },
        requestName: 'listRPNVersions',
        requestType: 'query',
      }),
      fetchGraphQLRequest<{ listRPNAssortments: RpnAssortment[] }>({
        query: listRPNAssortments,
        variables: { input: { rpn_id: rpnId } },
        requestName: 'listRPNAssortments',
        requestType: 'query',
      }),
      fetchGraphQLRequest<{ listRPNSkus: Skus[] }>({
        query: listRPNSkus,
        variables: { input: { rpn_id: rpnId } },
        requestName: 'listRPNSkus',
        requestType: 'query',
      }),
    ]);

    const listRPNDetailsResponse = listRPNDetailsResult.listRPNDetails || ({} as Omit<RPNDetails, 'agm_data'>);
    const rpnVersions = rpnVersionsResult.listRPNVersions || [];
    const rpnAssortments = rpnAssortmentsResult.listRPNAssortments || [];
    const rpnSkus = rpnSkusResult.listRPNSkus || [];

    logger.info(`Fetched RPN details for product line ${productLineId}, RPN ${rpnId} from API`);

    // Construct the agm_data object from the three separate API responses
    const agmData = {
      rpn_versions: rpnVersions,
      rpn_assortments: rpnAssortments,
      skus: rpnSkus,
    };

    return { ...listRPNDetailsResponse, agm_data: agmData };
  } catch (error) {
    logger.error(`Error fetching RPN details for product line ${productLineId}, RPN ${rpnId} from API:`, { error });
    throw formatApiError('Failed to fetch RPN details', error);
  }
};

// ============================================================================
// FPN Operations
// ============================================================================

/**
 * Fetches FPN details by product line ID, RPN ID, and FPN ID
 * @param productLineId - The ID of the product line
 * @param rpnId - The ID of the RPN
 * @param fpnId - The ID of the FPN to fetch
 * @returns Promise resolving to FPN details
 * @throws Error if API call fails
 */
export const fetchFPNDetailsAPI = async (productLineId: number, rpnId: number, fpnId: number): Promise<FPNDetails> => {
  try {
    logger.info(`Fetching FPN details for product line ${productLineId}, RPN ${rpnId}, FPN ${fpnId} from API`);

    // Fetch FPN details and SKUs in parallel for better performance
    const [listFPNDetailsResult, fpnSkusResult] = await Promise.all([
      fetchGraphQLRequest<{ listFPNDetails: Omit<FPNDetails, 'skus'> }>({
        query: listFPNDetails,
        variables: { input: { fpn_id: fpnId } },
        requestName: 'listFPNDetails',
        requestType: 'query',
      }),
      fetchGraphQLRequest<{ listFPNSkus: FPNSkus[] }>({
        query: listFPNSkus,
        variables: { input: { fpn_id: fpnId } },
        requestName: 'listFPNSkus',
        requestType: 'query',
      }),
    ]);

    const listFPNDetailsResponse = listFPNDetailsResult.listFPNDetails || ({} as Omit<FPNDetails, 'skus'>);
    const fpnSkus = fpnSkusResult.listFPNSkus || [];

    logger.info(`Fetched FPN details for product line ${productLineId}, RPN ${rpnId}, FPN ${fpnId} from API`);

    return { ...listFPNDetailsResponse, skus: fpnSkus };
  } catch (error) {
    logger.error(`Error fetching FPN details for product line ${productLineId}, RPN ${rpnId}, FPN ${fpnId} from API:`, {
      error,
    });
    throw formatApiError('Failed to fetch FPN details', error);
  }
};

/**
 * Updates RPN attributes (product_line_attributes, program_attributes, standard_attributes)
 * @param mutationData - The mutation payload containing upsert and delete operations
 * @returns Promise resolving to the number of records updated
 * @throws Error if API call fails
 */
export const updateRpnAttributesAPI = async (
  mutationData: RpnAttributesMutation
): Promise<{ numberOfRecordsUpdated: number }> => {
  try {
    logger.info('Updating RPN attributes', { rpnId: mutationData.rpn_id, mutationData });

    // Transform the mutation data to match the GraphQL input type
    const input = {
      product_line_id: mutationData.product_line_id,
      rpn_id: mutationData.rpn_id,
      updated_at: mutationData.updated_at,
      updated_by: mutationData.updated_by || '',
      upsert: mutationData.upsert.map((item) => ({
        attribute_type_label: item.attribute_type_label,
        attribute_value: item.attribute_value,
        comment: item.comment,
      })),
      delete: mutationData.delete.map((item) => ({
        attribute_type_label: item.attribute_type_label,
        attribute_value: item.attribute_value,
        comment: item.comment,
        // Include remove_attribute_type flag for complete attribute removal
        ...(item.remove_attribute_type && { remove_attribute_type: item.remove_attribute_type }),
      })),
    };

    const result = await fetchGraphQLRequest<{
      createOrUpdateRPNAttributes: MutationRecordCountResponse;
    }>({
      query: createOrUpdateRPNAttributes,
      variables: { input },
      requestName: 'createOrUpdateRPNAttributes',
      requestType: 'mutation',
      auditConfig: { pageName: 'post_attr_list_rpn' },
    });

    const numberOfRecordsUpdated = result.createOrUpdateRPNAttributes?.numberOfRecordsUpdated ?? 0;

    logger.info('RPN attributes updated successfully', {
      rpnId: mutationData.rpn_id,
      numberOfRecordsUpdated,
    });

    return { numberOfRecordsUpdated };
  } catch (error) {
    logger.error('Error updating RPN attributes:', { error, rpnId: mutationData.rpn_id });
    throw formatApiError('Failed to update RPN attributes', error);
  }
};

/**
 * Updates RPN overview (rpn_program_overview, documents_links)
 * @param mutationData - The mutation payload matching CreateOrUpdateRPNOverviewInput GraphQL type
 * @returns Promise resolving to the number of records updated
 * @throws Error if API call fails
 */
export const updateRpnOverviewAPI = async (
  mutationData: RpnOverviewMutation
): Promise<{ numberOfRecordsUpdated: number }> => {
  try {
    logger.info('Updating RPN overview', { rpnId: mutationData.rpn_id, mutationData });

    // Build the input matching the GraphQL CreateOrUpdateRPNOverviewInput type
    // The mutation data structure matches the GraphQL schema directly
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const input: any = {
      product_line_id: mutationData.product_line_id,
      rpn_id: mutationData.rpn_id,
      updated_at: mutationData.updated_at,
      updated_by: mutationData.updated_by || '',
    };

    // Add rpn_program_overview if present in mutation data
    if (mutationData.rpn_program_overview) {
      input.rpn_program_overview = mutationData.rpn_program_overview;
    }

    // Add documents_links if present in mutation data
    if (mutationData.documents_links) {
      input.documents_links = mutationData.documents_links;
    }

    // Add pm_owners (JSON string of alias array) if present
    if (mutationData.pm_owners !== undefined) {
      input.pm_owners = mutationData.pm_owners;
    }

    const result = await fetchGraphQLRequest<{
      createOrUpdateRPNOverview: MutationRecordCountResponse;
    }>({
      query: createOrUpdateRPNOverview,
      variables: { input },
      requestName: 'createOrUpdateRPNOverview',
      requestType: 'mutation',
      auditConfig: { pageName: 'rpn_update_overview' },
    });

    const numberOfRecordsUpdated = result.createOrUpdateRPNOverview?.numberOfRecordsUpdated ?? 0;

    logger.info('RPN overview updated successfully', {
      rpnId: mutationData.rpn_id,
      numberOfRecordsUpdated,
    });

    return { numberOfRecordsUpdated };
  } catch (error) {
    logger.error('Error updating RPN overview:', { error, rpnId: mutationData.rpn_id });
    throw formatApiError('Failed to update RPN overview', error);
  }
};

/**
 * Updates RPN additional info (manufacturing_info, market_info, specifications)
 * @param mutationData - The mutation payload matching CreateOrUpdateRPNAdditionalInfoInput GraphQL type
 * @returns Promise resolving to the number of records updated
 * @throws Error if API call fails
 */
export const updateRpnAdditionalInfoAPI = async (
  mutationData: RpnAdditionalInfoMutation,
  isFpn: boolean
): Promise<{ numberOfRecordsUpdated: number }> => {
  try {
    logger.info('Updating RPN additional info', { rpnId: mutationData.rpn_id, mutationData });

    // Build the input matching the GraphQL CreateOrUpdateRPNAdditionalInfoInput type
    // The mutation data structure matches the GraphQL schema directly
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const input: any = {
      is_root_program: !isFpn,
      product_line_id: mutationData.product_line_id,
      rpn_id: mutationData.rpn_id,
      updated_at: mutationData.updated_at,
      updated_by: mutationData.updated_by || '',
    };

    // Add manufacturing_info if present in mutation data
    if (mutationData.manufacturing_info) {
      input.manufacturing_info = mutationData.manufacturing_info;
    }

    // Add market_info if present in mutation data
    if (mutationData.market_info) {
      input.market_info = mutationData.market_info;
    }

    // Add specifications if present in mutation data
    if (mutationData.specifications) {
      input.specifications = mutationData.specifications;
    }

    const result = await fetchGraphQLRequest<{
      createOrUpdateRPNAdditionalInfo: MutationRecordCountResponse;
    }>({
      query: createOrUpdateRPNAdditionalInfo,
      variables: { input },
      requestName: 'createOrUpdateRPNAdditionalInfo',
      requestType: 'mutation',
      auditConfig: { pageName: 'rpn_update_additional_info' },
    });

    const numberOfRecordsUpdated = result.createOrUpdateRPNAdditionalInfo?.numberOfRecordsUpdated ?? 0;

    logger.info('RPN additional info updated successfully', {
      rpnId: mutationData.rpn_id,
      numberOfRecordsUpdated,
    });

    return { numberOfRecordsUpdated };
  } catch (error) {
    logger.error('Error updating RPN additional info:', { error, rpnId: mutationData.rpn_id });
    throw formatApiError('Failed to update RPN additional info', error);
  }
};

/**
 * Updates RPN Country Key Dates (key_dates, other_key_dates)
 * @param mutationData - The mutation payload matching CreateOrUpdateRPNCountryKeyDatesInput GraphQL type
 * @returns Promise resolving to the number of records updated
 * @throws Error if API call fails
 */
export const updateRpnCountryKeyDatesAPI = async (
  mutationData: RpnCountryKeyDatesMutation
): Promise<{ numberOfRecordsUpdated: number }> => {
  try {
    logger.info('Updating RPN country key dates', { rpnId: mutationData.rpn_id, mutationData });

    // Build the input matching the GraphQL CreateOrUpdateRPNCountryKeyDatesInput type
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const input: any = {
      is_root_program: mutationData.is_root_program,
      product_line_id: mutationData.product_line_id,
      rpn_id: mutationData.rpn_id,
      updated_at: mutationData.updated_at,
      updated_by: mutationData.updated_by || '',
    };

    // Add key_dates (country_street_dates) if present in mutation data
    if (mutationData.key_dates) {
      input.key_dates = mutationData.key_dates;
    }

    // Add other_key_dates if present in mutation data
    if (mutationData.other_key_dates) {
      input.other_key_dates = mutationData.other_key_dates;
    }

    const result = await fetchGraphQLRequest<{
      createOrUpdateRPNCountryKeyDates: MutationRecordCountResponse;
    }>({
      query: createOrUpdateRPNCountryKeyDates,
      variables: { input },
      requestName: 'createOrUpdateRPNCountryKeyDates',
      requestType: 'mutation',
      auditConfig: { pageName: 'rpn_update_country_key_dates' },
    });

    const numberOfRecordsUpdated = result.createOrUpdateRPNCountryKeyDates?.numberOfRecordsUpdated ?? 0;

    logger.info('RPN country key dates updated successfully', {
      rpnId: mutationData.rpn_id,
      numberOfRecordsUpdated,
    });

    return { numberOfRecordsUpdated };
  } catch (error) {
    logger.error('Error updating RPN country key dates:', { error, rpnId: mutationData.rpn_id });
    throw formatApiError('Failed to update RPN country key dates', error);
  }
};

// ============================================================================
// FPN Mutation Operations
// ============================================================================

/**
 * Updates FPN overview (fpn_program_overview)
 * @param mutationData - The mutation payload matching CreateOrUpdateFPNOverviewInput GraphQL type
 * @returns Promise resolving to the number of records updated
 * @throws Error if API call fails
 */
export const updateFpnOverviewAPI = async (
  mutationData: FpnOverviewMutation
): Promise<{ numberOfRecordsUpdated: number }> => {
  try {
    logger.info('Updating FPN overview', { fpnId: mutationData.fpn_id, mutationData });

    // Build the input matching the GraphQL CreateOrUpdateFPNOverviewInput type
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const input: any = {
      fpn_id: mutationData.fpn_id,
      product_line_id: mutationData.product_line_id,
      rpn_id: mutationData.rpn_id,
      updated_at: mutationData.updated_at,
      updated_by: mutationData.updated_by || '',
    };

    // Add fpn_program_overview if present in mutation data
    if (mutationData.fpn_program_overview) {
      input.fpn_program_overview = mutationData.fpn_program_overview;
    }

    const result = await fetchGraphQLRequest<{
      createOrUpdateFPNOverview: MutationRecordCountResponse;
    }>({
      query: createOrUpdateFPNOverview,
      variables: { input },
      requestName: 'createOrUpdateFPNOverview',
      requestType: 'mutation',
      auditConfig: { pageName: 'fpn_update_overview' },
    });

    const numberOfRecordsUpdated = result.createOrUpdateFPNOverview?.numberOfRecordsUpdated ?? 0;

    logger.info('FPN overview updated successfully', {
      fpnId: mutationData.fpn_id,
      numberOfRecordsUpdated,
    });

    return { numberOfRecordsUpdated };
  } catch (error) {
    logger.error('Error updating FPN overview:', { error, fpnId: mutationData.fpn_id });
    throw formatApiError('Failed to update FPN overview', error);
  }
};

/**
 * Updates FPN attributes (product_line_attributes, program_attributes, standard_attributes)
 * @param mutationData - The mutation payload containing upsert and delete operations
 * @returns Promise resolving to the number of records updated
 * @throws Error if API call fails
 */
export const updateFpnAttributesAPI = async (
  mutationData: FpnAttributesMutation
): Promise<{ numberOfRecordsUpdated: number }> => {
  try {
    logger.info('Updating FPN attributes', { fpnId: mutationData.fpn_id, mutationData });

    // Transform the mutation data to match the GraphQL input type
    const input = {
      product_line_id: mutationData.product_line_id,
      rpn_id: mutationData.rpn_id,
      fpn_id: mutationData.fpn_id,
      updated_at: mutationData.updated_at,
      updated_by: mutationData.updated_by || '',
      upsert: mutationData.upsert.map((item) => ({
        attribute_type_label: item.attribute_type_label,
        attribute_value: item.attribute_value,
        comment: item.comment,
      })),
      delete: mutationData.delete.map((item) => ({
        attribute_type_label: item.attribute_type_label,
        attribute_value: item.attribute_value,
        comment: item.comment,
      })),
    };

    const result = await fetchGraphQLRequest<{
      createOrUpdateFPNAttributes: MutationRecordCountResponse;
    }>({
      query: createOrUpdateFPNAttributes,
      variables: { input },
      requestName: 'createOrUpdateFPNAttributes',
      requestType: 'mutation',
      auditConfig: { pageName: 'fpn_post_attr_list' },
    });

    throwIfLambdaError(result.createOrUpdateFPNAttributes);
    const numberOfRecordsUpdated = result.createOrUpdateFPNAttributes?.numberOfRecordsUpdated ?? 0;

    logger.info('FPN attributes updated successfully', {
      fpnId: mutationData.fpn_id,
      numberOfRecordsUpdated,
    });

    return { numberOfRecordsUpdated };
  } catch (error) {
    logger.error('Error updating FPN attributes:', { error, fpnId: mutationData.fpn_id });
    throw formatApiError('Failed to update FPN attributes', error);
  }
};

/**
 * Updates FPN Country Key Dates (key_dates, other_key_dates)
 * @param mutationData - The mutation payload matching CreateOrUpdateFPNCountryKeyDatesInput GraphQL type
 * @returns Promise resolving to the number of records updated
 * @throws Error if API call fails
 */
export const updateFpnCountryKeyDatesAPI = async (
  mutationData: FpnCountryKeyDatesMutation
): Promise<{ numberOfRecordsUpdated: number }> => {
  try {
    logger.info('Updating FPN country key dates', { fpnId: mutationData.fpn_id, mutationData });

    // Build the input matching the GraphQL CreateOrUpdateFPNCountryKeyDatesInput type
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const input: any = {
      fpn_id: mutationData.fpn_id,
      is_root_program: mutationData.is_root_program,
      product_line_id: mutationData.product_line_id,
      rpn_id: mutationData.rpn_id,
      updated_at: mutationData.updated_at,
      updated_by: mutationData.updated_by || '',
    };

    // Add key_dates (country_street_dates) if present in mutation data
    if (mutationData.key_dates) {
      input.key_dates = mutationData.key_dates;
    }

    // Add other_key_dates if present in mutation data
    if (mutationData.other_key_dates) {
      input.other_key_dates = mutationData.other_key_dates;
    }

    const result = await fetchGraphQLRequest<{
      createOrUpdateFPNCountryKeyDates: MutationRecordCountResponse;
    }>({
      query: createOrUpdateFPNCountryKeyDates,
      variables: { input },
      requestName: 'createOrUpdateFPNCountryKeyDates',
      requestType: 'mutation',
      auditConfig: { pageName: 'fpn_update_country_key_dates' },
    });

    const numberOfRecordsUpdated = result.createOrUpdateFPNCountryKeyDates?.numberOfRecordsUpdated ?? 0;

    logger.info('FPN country key dates updated successfully', {
      fpnId: mutationData.fpn_id,
      numberOfRecordsUpdated,
    });

    return { numberOfRecordsUpdated };
  } catch (error) {
    logger.error('Error updating FPN country key dates:', { error, fpnId: mutationData.fpn_id });
    throw formatApiError('Failed to update FPN country key dates', error);
  }
};

/**
 * Updates FPN additional info (financial_info, manufacturing_info, market_info, specifications)
 * @param mutationData - The mutation payload matching CreateOrUpdateFPNAdditionalInfoInput GraphQL type
 * @returns Promise resolving to the number of records updated
 * @throws Error if API call fails
 */
export const updateFpnAdditionalInfoAPI = async (
  mutationData: FpnAdditionalInfoMutation
): Promise<{ numberOfRecordsUpdated: number }> => {
  try {
    logger.info('Updating FPN additional info', { fpnId: mutationData.fpn_id, mutationData });

    // Build the input matching the GraphQL CreateOrUpdateFPNAdditionalInfoInput type
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const input: any = {
      fpn_id: mutationData.fpn_id,
      is_root_program: mutationData.is_root_program,
      product_line_id: mutationData.product_line_id,
      rpn_id: mutationData.rpn_id,
      updated_at: mutationData.updated_at,
      updated_by: mutationData.updated_by || '',
    };

    // Add financial_info if present in mutation data (FPN only)
    if (mutationData.financial_info) {
      input.financial_info = mutationData.financial_info;
    }

    // Add manufacturing_info if present in mutation data
    if (mutationData.manufacturing_info) {
      input.manufacturing_info = mutationData.manufacturing_info;
    }

    // Add market_info if present in mutation data
    if (mutationData.market_info) {
      input.market_info = mutationData.market_info;
    }

    // Add specifications if present in mutation data
    if (mutationData.specifications) {
      input.specifications = mutationData.specifications;
    }

    const result = await fetchGraphQLRequest<{
      createOrUpdateFPNAdditionalInfo: MutationRecordCountResponse;
    }>({
      query: createOrUpdateFPNAdditionalInfo,
      variables: { input },
      requestName: 'createOrUpdateFPNAdditionalInfo',
      requestType: 'mutation',
      auditConfig: { pageName: 'fpn_update_additional_info' },
    });

    const numberOfRecordsUpdated = result.createOrUpdateFPNAdditionalInfo?.numberOfRecordsUpdated ?? 0;

    logger.info('FPN additional info updated successfully', {
      fpnId: mutationData.fpn_id,
      numberOfRecordsUpdated,
    });

    return { numberOfRecordsUpdated };
  } catch (error) {
    logger.error('Error updating FPN additional info:', { error, fpnId: mutationData.fpn_id });
    throw formatApiError('Failed to update FPN additional info', error);
  }
};
